/**
 * namingSession — hands-free bulk renaming (2026-08-28 ask).
 *
 * THE LOOP
 *   Renaming a box in GO is the naming studio's last mile, and it was still
 *   one app-switch per Pokémon: open Nexus Dex, copy, switch to GO, paste.
 *   A naming session removes the switching: start it, and the queue's first
 *   generated name is already on the clipboard. In GO: find the Pokémon
 *   (each step ALSO copies nothing the trainer must type — the name shown in
 *   the PiP window says who is next), rename, paste, then tap the floating
 *   PiP window's big CENTRE BUTTON and the NEXT name lands on the clipboard.
 *   The side skip arrows step forward/back through the queue. No keyboard,
 *   no app switching. (On-device 2026-08-29: with the old live-mode PiP the
 *   centre control drew as a square stop glyph; the native fix in
 *   NexPip3D.swift makes it a play/pause pair and lights up the arrows.
 *   Copy everywhere says "centre button" so both binaries read true.)
 *
 * HOW THE BUTTON REACHES US
 *   The NexPip3D native plugin forwards its AVKit transport callbacks as
 *   delegate events ("setPlaying:*" advance, "skipBy:forward"/"skipBy:back"
 *   next/previous) through the existing nexpip3d:event channel. Clipboard
 *   writes while backgrounded go through the plugin's UIPasteboard method
 *   (nexPip3D.setClipboardText) because the WebView clipboard needs focus;
 *   in the foreground the ordinary web path works and is tried second.
 *
 * WHO IS IN THE QUEUE
 *   Records whose stored nickname differs from their generated name (a null
 *   nickname counts as needing the rename), with a searchable generated name
 *   (isSearchableName: the same addressability rule the search strings use).
 *   Best IVs first: the keepers are the names most worth having.
 *
 * The session is ephemeral by design (module state, not storage): it lives
 * exactly as long as the renaming spree. Web gets the same session minus the
 * PiP: the in-app Next button does the copying.
 */
import { useSyncExternalStore } from "react";
import * as Pip3D from "@/lib/nexPip3D";
import { copyToClipboard } from "@/lib/copyToClipboard";
import {
  baseStatsForRecord,
  bestOptionOf,
  deriveMyPokemon,
  nicknameLeagueRanks,
  type MyPokemonDerived,
} from "@/lib/myPokemonDerived";
import {
  factsForRecord,
  nicknameCompareKey,
  renderNickname,
  templateWantsMoves,
  templateWantsPotential,
  templateWantsRanks,
  type NicknameFacts,
  type NicknameTemplate,
  type RenderedNickname,
} from "@/lib/ivNicknames";
import { bestPotentialFor } from "@/lib/bestPotential";
import { evolutionChainFor } from "@/lib/generated/speciesBaseStats.generated";
import { evolvedFormIdFor } from "@/lib/costumeEvolution";
import { cpmAtLevel } from "@/lib/ivSolver";
import { LEAGUE_CAPS, leagueFullRank, leaguePercent } from "@/lib/pvpRank";
import { powerUpCost } from "@/lib/powerUpCosts";
import { getSpeciesMovePool } from "@/lib/movePool";
import { moveNameKey } from "@/lib/moveTypeLookup";
import { isSearchableName } from "@/lib/myPokemonSearchStrings";
import {
  NICKNAME_TEMPLATE_CHANGE_EVENT,
  getNicknameTemplate,
  getNicknameTemplateState,
  presetTokensById,
  type NicknameTrigger,
} from "@/lib/nicknameTemplateStore";
import { isNative } from "@/lib/platform";
import type { MyPokemonRecord } from "@/lib/myPokemonStore";

export const NAMING_SESSION_EVENT = "nexus:naming-session-changed";

/** One queued rename: the record and the name to paste. */
export interface NamingQueueEntry {
  record: MyPokemonRecord;
  /** The generated name to paste into GO's rename box. */
  name: string;
  /** IV percent for the session card's context line (null never queues). */
  ivPercent: number | null;
  /** Best-option score and label (see bestOptionOf); drives the default
   *  ordering and the card/PiP context lines. */
  bestScore: number | null;
  bestLabel: string | null;
}

// bestOptionOf moved to myPokemonDerived (2026-08-30): the My Pokémon list
// ring shares it now. Re-exported so existing imports keep working.
export { bestOptionOf } from "@/lib/myPokemonDerived";

/**
 * A queue scope (2026-08-29 ask): which Pokémon the spree covers and in what
 * ORDER. The order is the whole trick: each scope's `goSearch` shows the same
 * set in GO, and with GO sorted as `note` says, the in-game list walks in
 * lockstep with the queue — the next name always belongs to the next card on
 * screen.
 */
export interface NamingScope {
  id: string;
  label: string;
  /** Paste-ready GO search selecting the same Pokémon, or null for all. */
  goSearch: string | null;
  /** How to line GO up with the queue, shown beside the copy button. */
  note: string | null;
  filter: (record: MyPokemonRecord, ivPercent: number | null) => boolean;
  compare: (a: NamingQueueEntry, b: NamingQueueEntry) => number;
}

/** Best OPTION first: a GL rank-1 outranks a 90% IV nothing (2026-08-30). */
const byBestDesc = (a: NamingQueueEntry, b: NamingQueueEntry) =>
  (b.bestScore ?? -1) - (a.bestScore ?? -1);
const byCpDesc = (a: NamingQueueEntry, b: NamingQueueEntry) =>
  (b.record.cp ?? -1) - (a.record.cp ?? -1);

export const NAMING_SCOPES: readonly NamingScope[] = [
  {
    id: "best",
    label: "Best first",
    goSearch: null,
    note: null,
    filter: () => true,
    compare: byBestDesc,
  },
  {
    id: "hundos",
    label: "Hundos",
    // GO's 4* appraisal band IS the hundo set.
    goSearch: "4*",
    note: "In GO: search 4*, sort by CP.",
    filter: (_r, ivPercent) => ivPercent === 100,
    compare: byCpDesc,
  },
  {
    id: "great",
    label: "Great League",
    goSearch: "cp-1500",
    note: "In GO: search cp-1500, sort by CP.",
    filter: (r) => r.cp != null && r.cp <= 1500,
    compare: byCpDesc,
  },
  {
    id: "ultra",
    label: "Ultra League",
    goSearch: "cp1501-2500",
    note: "In GO: search cp1501-2500, sort by CP.",
    filter: (r) => r.cp != null && r.cp > 1500 && r.cp <= 2500,
    compare: byCpDesc,
  },
];

export function namingScopeById(id: string | undefined): NamingScope {
  return NAMING_SCOPES.find((s) => s.id === id) ?? NAMING_SCOPES[0];
}

/**
 * Records still needing their generated name, in the scope's order (default:
 * everything, best IVs first). Pure and exported for tests;
 * startNamingSession drives it.
 */
export function buildNamingQueue(
  records: readonly MyPokemonRecord[],
  derive: (r: MyPokemonRecord) => MyPokemonDerived = deriveMyPokemon,
  scope: NamingScope = NAMING_SCOPES[0],
): NamingQueueEntry[] {
  const out: NamingQueueEntry[] = [];
  for (const record of records) {
    if (record.speciesId == null) continue;
    const derived = derive(record);
    if (!scope.filter(record, derived.ivPercent)) continue;
    const best = bestOptionOf(derived);
    const rendered = renderPreferredNickname(record, derived);
    if (!isSearchableName(rendered.name)) continue;
    // Already named — compared through nicknameCompareKey (2026-08-31): the
    // scanner reads a styled name back FLATTENED ("Zubat²²²" → "Zubat222"),
    // so a raw === here re-queued every correctly-renamed Pokémon forever.
    if (
      nicknameCompareKey(record.nickname) === nicknameCompareKey(rendered.name)
    ) continue;
    out.push({
      record,
      name: rendered.name,
      ivPercent: derived.ivPercent,
      bestScore: best?.score ?? null,
      bestLabel: best?.label ?? null,
    });
  }
  out.sort(scope.compare);
  return out;
}

export interface NamingSessionState {
  active: boolean;
  queue: NamingQueueEntry[];
  index: number;
  /** Whether the LAST copy landed (false = tell the trainer to copy in-app). */
  copied: boolean;
}

const IDLE: NamingSessionState = { active: false, queue: [], index: 0, copied: false };

let state: NamingSessionState = IDLE;
let unsubscribePip: (() => void) | null = null;
let unsubscribeTemplate: (() => void) | null = null;
/** Transport taps can double-fire (play toggles); collapse bursts. */
let lastAdvanceAt = 0;
/** Play-button advances keep the long debounce (toggles double-fire). */
const ADVANCE_DEBOUNCE_MS = 350;
/**
 * Deliberate navigation (skip arrows, in-app buttons) gets only a tiny
 * anti-double-fire window: "move 6 steps back" is six quick taps, and a
 * 350ms window was eating most of them (on-device 2026-08-30).
 */
const NAV_DEBOUNCE_MS = 150;

/** Injectable copier (tests). Returns whether the text landed. */
let copier: (text: string) => Promise<boolean> = async (text) => {
  // Native pasteboard first: it works from the background (the whole point
  // of the PiP flow) and equally in the foreground. Web clipboard second.
  if (isNative()) {
    if (await Pip3D.setClipboardText(text)) return true;
  }
  return copyToClipboard(text);
};

export function __setNamingSessionCopierForTest(
  fn: ((text: string) => Promise<boolean>) | null,
): void {
  copier = fn ?? (async (text) => copyToClipboard(text));
}

/** Put text on the clipboard the way the naming session does (native
 *  pasteboard first, so it works while Pokémon GO is in front). */
export function copyNameToClipboard(text: string): Promise<boolean> {
  return copier(text);
}

/**
 * Every fact a template could print for a record, computed only as far as
 * the template asks (ranks, the destiny sweep and the move pool each cost
 * something). Shared by the naming queue, the live scan's copy-on-read and
 * the Naming page's previews (2026-09-03).
 */
export function nicknameFactsForRecord(
  record: MyPokemonRecord,
  derived: MyPokemonDerived,
  template: NicknameTemplate,
): NicknameFacts {
  const wantsRanks = templateWantsRanks(template);
  const wantsPotential = templateWantsPotential(template);
  const wantsMoves = templateWantsMoves(template);
  const best = bestOptionOf(derived);
  const potential = wantsPotential ? bestPotentialFor(record, derived.level) : null;
  const base = baseStatsForRecord(record);
  const iv =
    record.ivAtk != null && record.ivDef != null && record.ivHp != null
      ? { atk: record.ivAtk, def: record.ivDef, sta: record.ivHp }
      : null;
  const ranks =
    wantsRanks && base && iv
      ? {
          ...nicknameLeagueRanks(record),
          masterRank: leagueFullRank("master", { base, iv }),
          littleRank:
            record.cp == null || record.cp <= LEAGUE_CAPS.little
              ? leagueFullRank("little", { base, iv })
              : null,
          littlePct: leaguePercent("little", { base, iv }),
        }
      : {};
  let targetHp: number | null = null;
  let costs: { dust: number; candy: number; candyXl: number } | null = null;
  if (potential && potential.targetLevel != null && iv) {
    const targetBase = baseStatsForRecord({
      speciesId: potential.speciesId,
      formId: potential.isCurrentForm ? record.formId : evolvedFormIdFor(record.formId, potential.speciesId),
      gender: record.gender,
    });
    if (targetBase) {
      targetHp = Math.max(10, Math.floor((targetBase.sta + iv.sta) * cpmAtLevel(potential.targetLevel)));
    }
    if (derived.level != null) {
      costs = powerUpCost(derived.level, potential.targetLevel, {
        lucky: record.isLucky,
        shadow: record.isShadow,
        purified: record.isPurified,
      });
    }
  }
  return factsForRecord(record, derived, {
    ...ranks,
    bestPct: best?.score ?? null,
    bestRank: potential?.rank ?? null,
    bestLeague: potential?.league ?? null,
    targetSpeciesName: potential?.speciesName ?? null,
    targetCp: potential?.targetCp ?? null,
    targetHp,
    targetPct: potential?.percent ?? null,
    dustCost: costs?.dust ?? null,
    candyCost: costs?.candy ?? null,
    xlCost: costs?.candyXl ?? null,
    hasEliteMove: wantsMoves ? recordHasEliteMove(record) : false,
  });
}

/** Whether any of the record's known moves is Elite or Legacy for its species. */
export function recordHasEliteMove(record: MyPokemonRecord): boolean {
  if (record.speciesId == null || record.moves.length === 0) return false;
  const pool = getSpeciesMovePool(record.speciesId, record.formId);
  const special = new Set(
    [...pool.fast, ...pool.charge]
      .filter((m) => m.availability === "elite" || m.availability === "legacy")
      .map((m) => moveNameKey(m.name)),
  );
  return record.moves.some((m) => special.has(moveNameKey(m.name)));
}

/** Does this Pokémon satisfy a conditional trigger? */
export function triggerMatches(
  t: NicknameTrigger,
  record: MyPokemonRecord,
  derived: MyPokemonDerived,
): boolean {
  switch (t.kind) {
    case "hundo":
      return record.ivAtk === 15 && record.ivDef === 15 && record.ivHp === 15;
    case "pvp": {
      // The Pokémon's own best league percent, OR what it becomes (2026-09-18
      // owner report: a 1/12/14 Nacli is an 84% Nacli and a 99.1% Garganacl,
      // and the destiny layout the trigger points at is about the latter).
      // bestPotentialFor is cached per record, so this costs one sweep once.
      const threshold = t.threshold ?? 98;
      const best = bestOptionOf(derived);
      if (best != null && best.league != null && best.score >= threshold) return true;
      const destiny = bestPotentialFor(record, derived.level);
      return destiny?.percent != null && destiny.percent >= threshold;
    }
    case "favourite":
      return record.tags.some((tag) => /^fav/i.test(tag));
    case "cp":
      return record.cp != null && record.cp > (t.threshold ?? 2500);
    case "fullyEvolved":
      return record.speciesId != null && evolutionChainFor(record.speciesId).length === 0;
    case "twoCharged":
      return record.moves.filter((m) => m.kind === "charged").length >= 2;
    case "highIv":
      return derived.ivPercent != null && derived.ivPercent >= (t.threshold ?? 90);
    default:
      return false;
  }
}

/**
 * The template this Pokémon should be named with: for Pro, the first
 * enabled trigger it satisfies picks that trigger's preset; otherwise, and
 * for everyone else, the layout selected on the Naming page.
 */
export function templateForRecord(
  record: MyPokemonRecord,
  derived: MyPokemonDerived,
  opts: { isPro: boolean },
): { template: NicknameTemplate; presetId: string } {
  const state = getNicknameTemplateState();
  if (opts.isPro) {
    for (const t of state.triggers) {
      if (!t.enabled || !triggerMatches(t, record, derived)) continue;
      const tokens = presetTokensById(t.presetId, state.userPresets);
      if (tokens) return { template: { tokens }, presetId: t.presetId };
    }
  }
  return { template: { tokens: state.tokens }, presetId: state.presetId };
}

/**
 * The trainer's preferred name for a record: the template that applies to
 * it (triggers for Pro) rendered against its facts. Shared by the naming
 * queue and the live scan's copy-on-read (2026-09-03).
 */
export function renderPreferredNickname(
  record: MyPokemonRecord,
  derived: MyPokemonDerived,
  opts: { isPro?: boolean } = {},
): RenderedNickname {
  const { template } = templateForRecord(record, derived, { isPro: opts.isPro === true });
  return renderNickname(template, nicknameFactsForRecord(record, derived, template));
}

function emit(): void {
  try {
    window.dispatchEvent(new Event(NAMING_SESSION_EVENT));
  } catch {
    /* SSR */
  }
}

function setState(next: NamingSessionState): void {
  state = next;
  emit();
}

async function updatePipHud(): Promise<void> {
  if (!isNative() || !state.active) return;
  const entry = state.queue[state.index];
  if (!entry) return;
  try {
    // Under the name: CP and the Pokémon's best case, so the HUD says
    // both WHICH card to find and WHY it queued this high ("CP 1500 ·
    // GL 100%", 2026-08-30 ask). Always sent, "" clears a stale one.
    const sub = [
      entry.record.cp != null ? `CP ${entry.record.cp}` : null,
      entry.bestLabel,
    ]
      .filter(Boolean)
      .join(" · ");
    await Pip3D.updateStatus({
      phase: "scanning",
      target: 1, // suppress the capture chip; this is not a scan
      message: entry.name,
      splitSub: sub,
      detail: `${state.index + 1} of ${state.queue.length}. Paste, then centre button for next.`,
      // Split layout (2026-08-29): the name HUGE in the top half, this
      // instruction centred in the bottom half. Sticky natively, so
      // endNamingSession sends "standard" to hand the HUD back to scans.
      layout: "split",
    });
  } catch {
    /* HUD text is nice-to-have */
  }
}

async function copyCurrent(): Promise<void> {
  const entry = state.queue[state.index];
  if (!entry) return;
  let ok = false;
  try {
    ok = await copier(entry.name);
  } catch {
    ok = false;
  }
  setState({ ...state, copied: ok });
  void updatePipHud();
}

/** Move the cursor and copy the new current name. Clamped to the queue. */
export async function advanceNamingSession(
  delta: 1 | -1 = 1,
  source: "play" | "nav" = "nav",
): Promise<void> {
  if (!state.active) return;
  const now = Date.now();
  const window = source === "play" ? ADVANCE_DEBOUNCE_MS : NAV_DEBOUNCE_MS;
  if (now - lastAdvanceAt < window) return;
  lastAdvanceAt = now;
  const next = Math.min(state.queue.length - 1, Math.max(0, state.index + delta));
  if (next === state.index && delta === 1 && state.index === state.queue.length - 1) {
    // Advancing past the end finishes the spree.
    await endNamingSession();
    return;
  }
  setState({ ...state, index: next });
  await copyCurrent();
}

/** Jump straight to a queue position and copy its name (the in-app list:
 *  "start here", or "back 6" by tapping where you actually are in GO). */
export async function jumpNamingSession(index: number): Promise<void> {
  if (!state.active) return;
  const next = Math.min(state.queue.length - 1, Math.max(0, index));
  if (next === state.index) {
    await copyCurrent();
    return;
  }
  lastAdvanceAt = Date.now();
  setState({ ...state, index: next });
  await copyCurrent();
}

/**
 * PiP transport events, untangled (on-device 2026-08-30: all three PiP
 * buttons advanced FORWARD). Tapping a skip arrow can fire a setPlaying
 * callback alongside skipByInterval; the play handler advanced +1 first and
 * the debounce then swallowed the real skipBy:back. So play advances are
 * PROVISIONAL: they wait a grace window, and a skip event arriving inside it
 * cancels the pending +1 and applies its own direction instead. A lone play
 * tap still advances, just a beat later. Exported as the one entry point so
 * tests can drive it without a native PiP.
 */
const PLAY_EVENT_GRACE_MS = 250;
let pendingPlayTimer: ReturnType<typeof setTimeout> | null = null;

export function handlePipTransportEvent(name: string): void {
  if (name === "skipBy:forward" || name === "skipBy:back") {
    if (pendingPlayTimer != null) {
      clearTimeout(pendingPlayTimer);
      pendingPlayTimer = null;
    }
    void advanceNamingSession(name === "skipBy:forward" ? 1 : -1, "nav");
    return;
  }
  if (name.startsWith("setPlaying")) {
    if (pendingPlayTimer != null) return; // a play advance is already queued
    pendingPlayTimer = setTimeout(() => {
      pendingPlayTimer = null;
      void advanceNamingSession(1, "play");
    }, PLAY_EVENT_GRACE_MS);
  }
}

function clearPendingPlay(): void {
  if (pendingPlayTimer != null) {
    clearTimeout(pendingPlayTimer);
    pendingPlayTimer = null;
  }
}

/** Re-copy the current name (the in-app "Copy again" button). */
export async function recopyNamingSession(): Promise<void> {
  if (!state.active) return;
  await copyCurrent();
}

/**
 * The queue snapshots its names when the session starts, so switching
 * presets mid-spree used to leave STALE names on the clipboard (on-device
 * 2026-08-30: "Sam's fav" selected, session still copying the old layout).
 * An active session listens for template saves and re-renders every queued
 * name in place — same records, same order, same position — then re-copies
 * the current one so the clipboard and PiP follow the new layout instantly.
 */
function refreshQueueNamesForTemplate(): void {
  if (!state.active || state.queue.length === 0) return;
  const template = getNicknameTemplate();
  const wantsRanks = templateWantsRanks(template);
  const wantsPotential = templateWantsPotential(template);
  const queue = state.queue.map((entry) => {
    const derived = deriveMyPokemon(entry.record);
    const potential = wantsPotential ? bestPotentialFor(entry.record, derived.level) : null;
    const rendered = renderNickname(
      template,
      factsForRecord(entry.record, derived, {
        ...(wantsRanks ? nicknameLeagueRanks(entry.record) : {}),
        bestPct: bestOptionOf(derived)?.score ?? null,
        bestRank: potential?.rank ?? null,
        bestLeague: potential?.league ?? null,
        targetSpeciesName: potential?.speciesName ?? null,
      }),
    );
    return { ...entry, name: rendered.name };
  });
  setState({ ...state, queue });
  void copyCurrent();
}

/**
 * Start a session over the given records. Returns the queue length (0 =
 * nothing to rename, no session started). On device it arms the PiP: the
 * window pops when the trainer swaps to GO, and its transport buttons drive
 * the queue.
 */
export async function startNamingSession(
  records: readonly MyPokemonRecord[],
  scope: NamingScope = NAMING_SCOPES[0],
): Promise<number> {
  const queue = buildNamingQueue(records, deriveMyPokemon, scope);
  if (queue.length === 0) return 0;
  lastAdvanceAt = 0;
  setState({ active: true, queue, index: 0, copied: false });

  // Preset switches mid-session re-render the queue (see the refresher).
  const onTemplateChange = () => refreshQueueNamesForTemplate();
  window.addEventListener(NICKNAME_TEMPLATE_CHANGE_EVENT, onTemplateChange);
  unsubscribeTemplate = () =>
    window.removeEventListener(NICKNAME_TEMPLATE_CHANGE_EVENT, onTemplateChange);

  if (isNative()) {
    try {
      await Pip3D.prepare();
      await Pip3D.setAutoInline(true);
      unsubscribePip = Pip3D.subscribeEvents((e) => {
        handlePipTransportEvent(e.event);
      });
    } catch {
      // PiP is the enhancement, not the session: in-app buttons still work.
    }
  }
  await copyCurrent();
  return queue.length;
}

export async function endNamingSession(): Promise<void> {
  clearPendingPlay();
  if (unsubscribePip) {
    try {
      unsubscribePip();
    } catch {
      /* ignore */
    }
    unsubscribePip = null;
  }
  if (unsubscribeTemplate) {
    try {
      unsubscribeTemplate();
    } catch {
      /* ignore */
    }
    unsubscribeTemplate = null;
  }
  if (isNative()) {
    try {
      // Hand the HUD back to the scan layout: the split flag is sticky
      // natively, and the next live scan's pushes never carry `layout`.
      await Pip3D.updateStatus({ phase: "scanning", target: 1, layout: "standard" });
      await Pip3D.setAutoInline(false);
      await Pip3D.stop();
    } catch {
      /* ignore */
    }
  }
  setState(IDLE);
}

/** Current session snapshot (tests + imperative callers). */
export function getNamingSessionState(): NamingSessionState {
  return state;
}

function getSnapshot(): NamingSessionState {
  return state;
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(NAMING_SESSION_EVENT, cb);
  return () => window.removeEventListener(NAMING_SESSION_EVENT, cb);
}

/** Reactive session state for the Naming page card. */
export function useNamingSession(): NamingSessionState {
  return useSyncExternalStore(subscribe, getSnapshot, () => IDLE);
}

/** Test seam. */
export function __resetNamingSessionForTest(): void {
  state = IDLE;
  lastAdvanceAt = 0;
  clearPendingPlay();
  if (unsubscribePip) {
    try {
      unsubscribePip();
    } catch {
      /* ignore */
    }
    unsubscribePip = null;
  }
  if (unsubscribeTemplate) {
    try {
      unsubscribeTemplate();
    } catch {
      /* ignore */
    }
    unsubscribeTemplate = null;
  }
}
